import React, { useEffect, useState } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from 'recharts';
import { api } from '../api';
import type { SystemStats, TokenUsage } from '../types';

export default function Overview() {
  const [stats, setStats] = useState<SystemStats | null>(null);
  const [tokenData, setTokenData] = useState<TokenUsage['daily_breakdown']>([]);
  const [error, setError] = useState('');

  useEffect(() => {
    Promise.all([
      api<SystemStats>('/admin/system/stats'),
      api<TokenUsage>('/admin/tokens/usage?period=month'),
    ])
      .then(([s, t]) => {
        setStats(s);
        setTokenData(t.daily_breakdown || []);
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, []);

  if (error) return <div className="error-msg" style={{ margin: 24 }}>{error}</div>;
  if (!stats) return <div className="loading-state"><div className="spinner" /></div>;

  return (
    <div className="page-content">
      <div style={{ marginBottom: 24 }}>
        <div className="overline">Admin</div>
        <h1 className="page-title">System Overview</h1>
      </div>

      {/* Users */}
      <div className="section">
        <div className="section-header"><span className="section-title">Users</span></div>
        <div className="kpi-grid kpi-grid-5">
          <div className="kpi-cell">
            <div className="kpi-label">Total</div>
            <div className="kpi-value">{stats.users.total.toLocaleString()}</div>
          </div>
          <div className="kpi-cell">
            <div className="kpi-label">Active 7d</div>
            <div className="kpi-value accent">{stats.users.active_last_7d.toLocaleString()}</div>
          </div>
          <div className="kpi-cell">
            <div className="kpi-label">Active 30d</div>
            <div className="kpi-value">{stats.users.active_last_30d.toLocaleString()}</div>
          </div>
          <div className="kpi-cell">
            <div className="kpi-label">New 7d</div>
            <div className="kpi-value ok">{stats.users.new_last_7d.toLocaleString()}</div>
          </div>
          <div className="kpi-cell">
            <div className="kpi-label">New 30d</div>
            <div className="kpi-value">{stats.users.new_last_30d.toLocaleString()}</div>
          </div>
        </div>
      </div>

      {/* Platform */}
      <div className="section">
        <div className="section-header"><span className="section-title">Platform</span></div>
        <div className="kpi-grid kpi-grid-4">
          <div className="kpi-cell">
            <div className="kpi-label">Dashboards</div>
            <div className="kpi-value">{stats.dashboards.total.toLocaleString()}</div>
          </div>
          <div className="kpi-cell">
            <div className="kpi-label">Tokens Today</div>
            <div className="kpi-value">{stats.tokens.today.toLocaleString()}</div>
          </div>
          <div className="kpi-cell">
            <div className="kpi-label">Tokens This Month</div>
            <div className="kpi-value">{stats.tokens.this_month.toLocaleString()}</div>
          </div>
          <div className="kpi-cell">
            <div className="kpi-label">Est. Cost (Month)</div>
            <div className="kpi-value alert">${stats.tokens.estimated_cost_usd.toFixed(4)}</div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid-2">
        <div className="card">
          <div className="card-title">Daily Token Usage (30 Days)</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={tokenData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-s)" vertical={false} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 10, fill: 'var(--tx3)' }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis tick={{ fontSize: 10, fill: 'var(--tx3)' }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border)', boxShadow: 'none' }}
                cursor={{ fill: 'var(--bg3)' }}
              />
              <Bar dataKey="tokens" fill="var(--accent)" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="card">
          <div className="card-title">Email Delivery</div>
          <div className="stat-row">
            <span className="stat-row-label">Sent Today</span>
            <span className="stat-row-value">{stats.emails.sent_today.toLocaleString()}</span>
          </div>
          <div className="stat-row">
            <span className="stat-row-label">Sent This Week</span>
            <span className="stat-row-value">{stats.emails.sent_this_week.toLocaleString()}</span>
          </div>
          <div className="stat-row">
            <span className="stat-row-label">Delivery Rate</span>
            <span className="stat-row-value">{(stats.emails.delivery_rate * 100).toFixed(1)}%</span>
          </div>

          <div className="card-title" style={{ marginTop: 20 }}>Most Active Dashboards</div>
          {stats.dashboards.most_active.length === 0 ? (
            <div style={{ fontSize: 12, color: 'var(--tx3)', padding: '8px 0' }}>No data</div>
          ) : stats.dashboards.most_active.map((d, i) => (
            <div key={d.id} className="stat-row">
              <span
                className="stat-row-label"
                style={{ overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '72%', whiteSpace: 'nowrap' }}
              >
                <span style={{ color: 'var(--tx4)', marginRight: 8, fontSize: 11 }}>#{i + 1}</span>
                {d.name}
              </span>
              <span className="stat-row-value">{d.msg_count} msgs</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
